import i18n from 'src/utils/translate';
import styled from 'styled-components';
import { textSmall } from 'styles-js/mixins/typography';

export type ImageUploadProps = {
  description: string,
  url: string,
  onPreviewRemove: () => void,
  onAddImageDescription: () => void,
};

export default function ImageUpload({
  description, url, onPreviewRemove, onAddImageDescription,
}: ImageUploadProps): JSX.Element {
  return (
    <Container>
      <ImgContainer>
        <Img alt={description} data-test-id="image-upload-image"
          src={url} />
        <RemoveButton
          aria-label={i18n.t('Remove this image')}
          className="btn"
          data-test-id="remove-image-button"
          type="button"
          onClick={onPreviewRemove}
        >
          {i18n.t('Remove')}{' '}<Icon className="icon icon-close" />
        </RemoveButton>
      </ImgContainer>

      {description && <Caption data-test-id="image-description">{description}</Caption>}

      <DescriptionButton
        className="btn"
        data-test-id="add-image-description-button"
        type="button"
        onClick={onAddImageDescription}
      >
        {i18n.t('Edit description')}
      </DescriptionButton>
    </Container>
  );
}

const Container = styled.div`
  padding: 10px 10px 12px;
`;

const ImgContainer = styled.div`
  position: relative;
  display: inline-block;
  min-width: 118px;
  min-height: 54px;
  margin-bottom: 10px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  margin: 0;
  padding: 5px 8px 7px;
  border-radius: 4px;
  border: ${({theme}) => theme.greyLine};
  background-color: ${({theme}) => theme.colorWhite};
  box-shadow: 0 0 6px ${({theme}) => theme.colorBlackHover};
  transition: all 0.5s ease;

  &:focus, &:hover, &:active {
    border-color: ${({theme}) => theme.colorBlue};
  }
`;

const Img = styled.img`
  max-width: 100%;
`;

const Icon = styled.span`
  color: ${({theme}) => theme.colorGreyDark};
`;

const DescriptionButton = styled.button`
  display: block;
  margin: 5px 0 0 0;
  padding: 5px 8px 7px;
  background-color: ${({theme}) => theme.colorWhite};
  box-shadow: ${({theme}) => theme.shadowLight};
  border-color: ${({theme}) => theme.colorGreyLight};

  &:focus, &:hover, &:active {
    border-color: ${({theme}) => theme.colorBlue};
  }
`;

const Caption = styled.p`
  ${textSmall};
  color: $color-grey-dark;
`;
